/*global snowplow,StudyPortals*/

/**
 * Uses the Snowplow JavaScript tracker to capture user behaviour
 */

'use strict';

import Configuration from '../Configuration';
import trackingViewsImpressionsHandler from './../Tracking/TrackingViewsImpressionsHandler';
import trackingDataHandler from './../Tracking/TrackingDataHandler';
import { SnowplowUserIdentifierService } from './SnowplowUserIdentifierService';

class SPSnowplowTracker {
	static init() {
		if (typeof snowplow === 'function') {
			snowplow(() => {
				// Snowplow javascript is already loaded
				SPSnowplowTracker.run();
			});

			return;
		}

		document.addEventListener('snowplow_available', () => {
			snowplow(() => {
				SPSnowplowTracker.run();
			});
		});
	}

	static run() {
		// the name which will be used in global namespace to attach this tracker API
		this.trackerGlobalNameSpaceName = 'DataTracker';

		if (typeof window[this.trackerGlobalNameSpaceName] === 'object') {
			// Tracker already registered
			return true;
		}

		const connectUrl = this.collectorEndpoint(Configuration.snowplow.collectorSubdomain, window.location.hostname);

		if (!connectUrl) {
			return;
		}

		this.initializeNewTracker(connectUrl);

		this.setUpGlobalContext();

		try {
			const studentIdentifierService = new SnowplowUserIdentifierService(this);
			studentIdentifierService.loadStudentIdentityId();
		} catch (err) {
			snowplow('trackError', {
				message: err.message + ' (Snowplow.js/setStudentId)',
				filename: 'Snowplow.js',
				error: err
			});
		}

		this.setUpCustomUrls();
		this.setUpLinkTracking();

		/* Enable cross domain tracking also for links dynamically added after page load
		such as in recommendation and wishlist: */
		document.addEventListener('links_rendered', () => {
			snowplow('crossDomainLinker', this.decorateLink);
		});

		snowplow('enableErrorTracking', {
			contextAdder: this.contextAdder.bind(this)
		});

		try {
			snowplow('enableActivityTracking', {
				minimumVisitLength: 20,
				heartbeatDelay: 20
			});
		} catch (error) {
			snowplow('trackError', {
				message: error.message + ' (Snowplow.js/enableActivityTracking)',
				filename: 'Snowplow.js',
				error: error
			});
		}

		try {
			snowplow('trackPageView');
		} catch (error) {
			snowplow('trackError', {
				message: error.message + ' (Snowplow.js/trackPageView)',
				filename: 'Snowplow.js',
				error: error
			});
		}
		this.setupDataTrackingApi();

		this.form();

		document.addEventListener('click', this.trackClickableDiv);

		this.portallog();
		this.ga();
		this.search();
		this.hotjar();
		this.buttonClick();
		this.loadedTrackers();
		this.enrichedLinkClicks();
		this.adblocker();
	}

	static setupDataTrackingApi() {
		const DataTrackerApi = {
			trackStructEvent: this.trackStructEvent.bind(this)
		};

		// register tracker to global namespace
		window[this.trackerGlobalNameSpaceName] = DataTrackerApi;

		// dispatch the DataTrackerReady event
		const event = new CustomEvent('DataTrackerReady', {
			bubbles: true,
			cancelable: false,
			details: {
				tracker: DataTrackerApi
			}
		});
		document.dispatchEvent(event);
	}

	static setUpLinkTracking() {
		try {
			snowplow('enableLinkClickTracking', { pseudoClicks: true });
		} catch (error) {
			snowplow('trackError', {
				message: error.message + ' (Snowplow.js/enableLinkClickTracking)',
				filename: 'Snowplow.js',
				error: error
			});
		}

		setTimeout(function () {
			try {
				snowplow('refreshLinkClickTracking');
			} catch (error) {
				snowplow('trackError', {
					message: error.message + ' (Snowplow.js/refreshLinkClickTracking)',
					filename: 'Snowplow.js',
					error: error
				});
			}
		}, 1000);
	}

	static initializeNewTracker(connectUrl) {
		snowplow('newTracker', 'cf7_1.12.1', connectUrl, {
			appId: Configuration.snowplow.app_id,
			platform: 'web',
			cookieDomain: null,
			discoverRootDomain: true,
			encodeBase64: false,
			respectDoNotTrack: false,
			userFingerprint: true,
			userFingerprintSeed: 6385926734,
			sessionCookieTimeout: 3600,
			eventMethod: 'post',
			postPath: '/com.studyportals/v1',
			maxPostBytes: 5000,
			contexts: {
				webPage: true,
				performanceTiming: true,
				gaCookies: true,
				geolocation: false
			},
			crossDomainLinker: this.decorateLink
		});
	}

	/*
	 * Returns true if the link element points to
	 * one of our websites (other than the current one)
	 * */

	static decorateLink(linkElement) {
		const destinationDomain = new URL(linkElement.href).hostname;

		if (window.location.hostname === destinationDomain) {
			return false;
		}

		const regex = new RegExp('\\w{3,}\\.\\w+portal\\.(com|fyi)$');

		return regex.test(destinationDomain) || ['sl.prtl.co', 'sl.tst.prtl.co'].includes(destinationDomain);
	}

	/*
	 * Determine which collector endpoint to use based on the page's hostname
	 * We do this to ensure the Snowplow collector sets a first-party cookie
	 * */

	static collectorEndpoint(collectorSubdomain, hostname) {
		const parts = hostname.split('.').reverse();

		const domain = parts[1];
		const extension = parts[0];

		const validHostname = domain && domain.endsWith('portal') && ['com', 'fyi'].includes(extension);

		if (validHostname) {
			return `${collectorSubdomain}.${domain}.${extension}`;
		}
	}

	/**
	 * Recent Snowplow enricher does not accept
	 * pipe character or e.g. "der=relevance?#content:visa
	 * So we encode URL and ref URL and manually set them in the tracker
	 *
	 * We retrieve the full URL including query string from a session storage
	 * item because just using e.g. document.referrer would yield only the URL path
	 */

	static setUpCustomUrls() {
		const update = () => {
			this.setCustomUrl();
			this.setReferralUrl();
		};

		update();

		// Update custom URLs when the URL changes (e.g. tabbed content on study page)
		window.addEventListener('hashchange', update);
	}

	/**
	 * Returns the fixed URL
	 *
	 * Users sometimes navigate to pages with a URL that doesn't follow the URL standard.
	 * This function handles some known edge cases to prevent the Snowplow Enricher from
	 * throwing errors.
	 *
	 * @param {string} url
	 * @returns {string} Fixed URL
	 */
	static fixUrl(url) {
		let cleanUrl = url;

		// Swaps around the hash and query string if they're in the wrong order
		cleanUrl = this.ensureValidFragmentAndQueryOrder(cleanUrl);

		// Ensures all special characters are encoded
		cleanUrl = this.encodeOnce(cleanUrl);

		return cleanUrl;
	}

	/**
	 * Overrides Snowplow's default current URL
	 */
	static setCustomUrl() {
		try {
			const currentPage = this.fixUrl(window.location.href);

			snowplow('setCustomUrl', currentPage);
		} catch (error) {
			snowplow('trackError', {
				message: error.message + ' (Snowplow.js/setCustomUrl)',
				filename: 'Snowplow.js',
				error: error
			});
		}
	}

	/**
	 * Overrides Snowplow's default referrer URL
	 */
	static setReferralUrl() {
		try {
			let previousPage = this.getDocReferrer();

			if (previousPage !== '') {
				previousPage = this.fixUrl(previousPage);

				// In the past, we found that the Enricher only supported referrers up to 255 character.
				// TODO: Check if new versions of the Enricher supports >255 characters
				previousPage = this.shortEncoder(previousPage);
			}

			snowplow('setReferrerUrl', previousPage);
		} catch (error) {
			snowplow('trackError', {
				message: error.message + ' (Snowplow.js/setReferrerUrl)',
				filename: 'Snowplow.js',
				error: error
			});
		}
	}

	static setUserId(id) {
		snowplow('setUserId', id);
	}

	static clearUserId() {
		snowplow('setUserId', '');
	}

	static trackServiceUnavailable(service) {
		snowplow('trackStructEvent', {
			category: 'BigData',
			action: 'InitializeUserId',
			label: 'ServiceUnavailable',
			property: service
		});
	}

	static shortEncoder(urlstring) {
		if (urlstring.length > 255) {
			urlstring = decodeURIComponent(urlstring);
			urlstring = urlstring.slice(0, 246);

			while (encodeURI(urlstring).length > 246) {
				urlstring = urlstring.slice(0, -1);
			}

			urlstring += '…';
			urlstring = encodeURI(urlstring);
		}
		return urlstring;
	}

	/**
	 * Returns the document referrer
	 *
	 * @returns {string}
	 */
	static getDocReferrer() {
		let referrer;

		// Implements Snowplow's logic to find the document referrer
		// See https://github.com/snowplow/snowplow-javascript-tracker/blob/3ba4214282635d9a7ff7c053e5d3a597b1e6eb63/libraries/browser-tracker-core/src/helpers.ts#L149-L162
		try {
			referrer = window.top.document.referrer;
		} catch (e) {
			if (window.parent) {
				try {
					referrer = window.parent.document.referrer;
				} catch (e2) {
					referrer = '';
				}
			}
		}
		if (referrer === '') {
			referrer = document.referrer;
		}
		return referrer;
	}

	/**
	 * Ensures that the URL fragment goes *after* the query
	 *
	 * This can happen when paid traffic is sent to a search page. e.g. Google
	 * appends UTM tags as a URL query even if the URL contains a fragment,
	 * resulting in a fragment followed by a query, which cannot be parsed by
	 * the Snowplow enricher
	 *
	 * @param {String} urlStr URL with potentially a fragment before a query
	 * @returns {String} Valid URL as far as the fragment and query order is concerned
	 */
	static ensureValidFragmentAndQueryOrder(urlStr) {
		const url = new URL(urlStr);

		if (this.hashHasQuery(url.hash)) {
			return this.swapFragmentAndQuery(url);
		}

		return urlStr;
	}

	/**
	 * Returns whether an URL hash contains a query
	 * (identified by a question mark)
	 *
	 * @param {String} urlHash AKA URL fragment
	 * @returns {Boolean}
	 */
	static hashHasQuery(urlHash) {
		return urlHash.indexOf('?') !== -1;
	}

	/**
	 * Swaps an URL's fragment and query
	 *
	 * @param {URL} url An URL object with a query inside of its fragment
	 * @returns {String} Resulting URL as a string
	 */
	static swapFragmentAndQuery(url) {
		const splitArray = url.hash.split('?');

		url.hash = splitArray[0];

		url.search = '?' + splitArray[1];

		return url.toString();
	}

	static encodeOnce(url) {
		// In case the URL is partially encoded, first decode and then encode again.
		url = decodeURIComponent(url);
		return encodeURI(url);
	}

	static contextAdder(errorEvent) {
		try {
			if (!errorEvent.target || !errorEvent.target.src) {
				// Property not defined
				return [];
			}

			const context = {
				schema: 'iglu:com.studyportals/application_error_custom_context/jsonschema/1-0-4',
				data: {
					scriptSource: errorEvent.target.src
				}
			};

			return [context];
		} catch (error) {
			snowplow('trackError', {
				message: error.message + ' (Snowplow.js/errorEventContextAdder)',
				filename: 'Snowplow.js',
				error: error
			});
		}
	}

	static trackStructEvent(category, action, label, property, value) {
		snowplow('trackStructEvent', {
			category: category,
			action: action,
			label: label,
			property: property,
			value: value
		});
	}

	/*
	 *
	 * Enables form tracking
	 *
	 * */
	static form() {
		snowplow('enableFormTracking', {
			options: {
				forms: {
					denylist: []
				},
				fields: {
					denylist: ['password']
				}
			}
		});
	}

	/**
	 * Tracks clicks on elements with the "data-clickable"
	 * attribute.
	 * In the event, send information about the child <a>
	 */
	static trackClickableDiv(event) {
		if (event.target.hasAttribute('data-clickable')) {
			const linkEl = event.target.querySelector('a');

			const url = linkEl.getAttribute('href');

			snowplow('trackLinkClick', {
				targetUrl: url,
				elementId: linkEl.id,
				elementClasses: Array.from(linkEl.classList),
				elementTarget: '',
				elementContent: linkEl.textContent
			});
		}
	}

	/**
	 * Tracks portallog events to the DataTracker. These events
	 * are from the "old" statistical system that are recorded
	 * for legacy compatibility purposes.
	 *
	 * Besides legacy it contains search results tracking, which
	 * is not implemented anywhere else so is pretty nice I guess.
	 *
	 * @author Simon Nouwens <simon@studyportals.com>
	 */
	static portallog() {
		const propName = '_portallog_snplw_tracker_initiated';
		if (window[propName]) {
			return false;
		}
		window[propName] = true;

		this.trackedFactStore = [];

		this.registerPortalLogTracker();
	}

	static trackPortalLogEventHelper(event) {
		if (typeof event === 'undefined') {
			return true;
		}

		if (typeof event === 'string') {
			event = JSON.parse(event);
		}

		for (let i = 0; i < event.length; i++) {
			if (typeof event[i]['lo'] === 'undefined') {
				event[i]['lo'] = null;
			}
		}

		const logData = trackingDataHandler.retrieveTrackingDetails();

		if (logData === null) {
			return false;
		}

		const spTracker = this;

		if (JSON.stringify(spTracker.trackedFactStore).indexOf(JSON.stringify(event)) > -1) {
			// event is already tracked
			return false;
		}
		spTracker.trackedFactStore.push(event);
		snowplow('trackSelfDescribingEvent', {
			event: {
				schema: 'iglu:com.studyportals/portallog/jsonschema/1-0-8',
				data: {
					facts: event,
					last_search: logData.search
				}
			}
		});
	}

	/**
	 * Registers portallog tracker: waits for TrackingViewsImpressionsHandler.send() to be called.
	 * It also reads the tracking's factsHistory, if present, which is useful
	 * in case this script loads after Tracking events
	 * have already been triggered.
	 * @returns {boolean}
	 */
	static registerPortalLogTracker() {
		window.addEventListener('factsSent', (event) => {
			this.trackPortalLogEventHelper(event.detail);
		});

		if (typeof trackingViewsImpressionsHandler.factsHistory === 'object') {
			for (let i = 0; i < trackingViewsImpressionsHandler.factsHistory.length; i++) {
				// this needs a little transformation into the desired format
				const decodedHistFact = JSON.parse(trackingViewsImpressionsHandler.factsHistory[i]);
				const facts = this.formatFact(decodedHistFact);
				this.trackPortalLogEventHelper(facts);
			}
		}
	}

	static formatFact(decodedHistFact) {
		return decodedHistFact.f.map((x) => {
			const fact = x;
			fact['lo'] = decodedHistFact['l'];
			return fact;
		});
	}

	/**
	 *
	 * Adds structured event triggering to clicking on elements that have a
	 * data-ga-tracking attribute, used to communicate with google
	 * analytics.
	 *
	 * @author Simon Nouwens <simon@studyportals.com>
	 */
	static ga() {
		const propName = '_ga_snplw_tracker_initiated';
		if (window[propName]) {
			return false;
		}
		window[propName] = true;

		const selector = document.querySelectorAll('[data-ga-tracking]');

		for (let i = selector.length - 1; i >= 0; i--) {
			selector[i].addEventListener('click', this.gaTrack.bind(this));
		}
	}

	/**
	 * @param target Element that was clicked
	 */
	static gaTrack(target) {
		target = target.target;
		let fact =
			target.getAttribute('data-ga-tracking') ||
			this.getClosest(target, '[data-ga-tracking]').getAttribute('data-ga-tracking');
		fact = JSON.parse(fact);

		const category = typeof fact['category'] === 'string' ? fact.category : null;
		const action = typeof fact['action'] === 'string' ? fact.action : null;
		const label = typeof fact['label'] === 'string' ? fact.label : null;
		const property = typeof fact['property'] === 'string' ? fact.property : null;
		let value = typeof fact['value'] === 'string' ? fact.value : null;

		// A structured event "value" field must be numerical
		if (isNaN(value)) {
			value = null;
		}

		snowplow('trackStructEvent', {
			category: category,
			action: action,
			label: label,
			property: property,
			value: value
		});
	}

	/**
	 * Get closest parent which matches selector
	 *
	 * @param {object} elem
	 * @param {string} selector
	 */
	static getClosest(elem, selector) {
		for (; elem && elem !== document; elem = elem.parentNode) {
			if (elem.matches(selector)) return elem;
		}
		return null;
	}

	/**
	 * Tracks structured event when a search is completed
	 *
	 * @author Simon Nouwens <simon@studyportals.com>
	 */
	static search() {
		const propName = '_search_snplw_tracker_initiated';
		if (window['propName']) {
			return false;
		}
		window[propName] = true;

		setInterval(function () {
			snowplow('refreshLinkClickTracking');
		}, 500);

		const spTracker = this;

		document.addEventListener('search_complete', function (data) {
			spTracker.trackSearchComplete(data);
		});

		document.addEventListener('url_change', function (data) {
			try {
				snowplow('trackStructEvent', {
					category: 'search',
					action: 'init',
					label: data.detail.searchRequest.urlHash,
					property: '',
					value: ''
				});
			} catch (err) {
				snowplow('trackError', {
					message: err.message,
					filename: 'Snowplow.js',
					error: err
				});
			}
		});

		try {
			const searchRequest = StudyPortals.Search.searchController.searchRequest;
			const curRequest = StudyPortals.Search.searchController.searchRequest.currentRequest;
			// the current request is already running, so we actually missed the url change
			const urlHash = searchRequest.urlHash;
			snowplow('trackStructEvent', {
				category: 'search',
				action: 'init',
				label: urlHash,
				property: '',
				value: ''
			});

			curRequest.then(function (data) {
				spTracker.trackSearchComplete(data);
			});
		} catch (e) {
			// this is not defined yet, meaning there is no request, and the search_complete event listener will catch this
		}
	}

	static trackSearchComplete(data) {
		try {
			const resultSize = typeof data['total'] === 'string' ? data.total : '';
			snowplow('trackStructEvent', {
				category: 'search',
				action: 'complete',
				label: data['requestParameters'],
				property: resultSize,
				value: ''
			});
			snowplow('refreshLinkClickTracking');
			setTimeout(function () {
				snowplow('refreshLinkClickTracking');
			}, 100);
		} catch (err) {
			snowplow('trackError', {
				message: err.message,
				filename: 'Snowplow.js',
				error: err
			});
		}
	}

	/**
	 * Registers the HotJar tracker to the DataTracker,
	 * tracks all user answers as structured event.
	 *
	 * @author Simon Nouwens <simon@studyportals.com>
	 */
	static hotjar() {
		const propName = '_hotjar_snplw_tracker_initiated';
		if (window['propName']) {
			return false;
		}
		window[propName] = true;

		this.alreadyTrackedHotJarQuestions = [];

		this.overrideSend(XMLHttpRequest.prototype.send);
	}

	static processEvent(event) {
		try {
			for (let i = 0; i <= event['answers'].length; i++) {
				const answer = event['answers'][i];
				if (this.alreadyTrackedHotJarQuestions.indexOf(answer['question']) > -1) {
					continue;
				}
				snowplow('trackStructEvent', {
					category: 'hotjar',
					action: 'user_answer',
					label: answer['question'].substring(0, 256),
					property: answer['answer']
				});
				this.alreadyTrackedHotJarQuestions.push(answer['question']); // keep a store so we don't double track
			}
		} catch (e) {
			return false;
		}
	}

	static overrideSend(send) {
		const spTracker = this;

		XMLHttpRequest.prototype.send = function (dataClean) {
			try {
				const data = JSON.parse(dataClean);
				if (data.action !== undefined) {
					if (data.action === 'create_poll_response' || data.action === 'update_poll_response') {
						spTracker.processEvent(JSON.parse(data['response_content']));
					}
				}
				// eslint-disable-next-line no-empty
			} catch (e) {}

			send.call(this, dataClean);
		};
	}

	/**
	 * Tracks unstructured events when a button is clicked
	 *
	 * @author Boris Morel <morel@studyportals.com>
	 * @author Frank Kluijtmans <frank@studyportals.com>
	 */

	static buttonClick() {
		// Make sure that we don't initialize this tracker twice
		const propName = '_btn_click_snplw_tracker_initiated';
		if (window[propName]) {
			// btn click tracking already initiated'
			return;
		}
		window[propName] = true;

		/*
		Listen to all clicks on the page and then keep only
		clicks on <button> elements.
		So we capture clicks on dynamically created buttons
		*/
		document.addEventListener('click', this.trackButtonClick);
	}

	static trackButtonClick(event) {
		let button = null;
		let attrDataId = null;

		if (event.target.tagName === 'BUTTON') {
			button = event.target;
			attrDataId = event.target.getAttribute('data-id');
		} else if (event.target.parentNode !== null) {
			/* Handle e.g. close button in a wishlist card
			  the clicked element is a <i> and the parent
			  element is <button>: */
			if (event.target.parentNode.tagName === 'BUTTON') {
				button = event.target.parentNode;
				attrDataId = event.target.parentNode.getAttribute('data-id');
			}
		}

		if (button !== null) {
			const data = {
				elementClasses: button['className'],
				elementId: button['id'],
				attrDataId: attrDataId,
				innerText: button['innerText']
			};

			snowplow('trackSelfDescribingEvent', {
				event: {
					schema: 'iglu:com.studyportals/button_click/jsonschema/1-0-2',
					data: data
				}
			});
		}
	}

	/**
	 *
	 * Track whether other trackers are loaded
	 *
	 * @author Boris Morel <morel@studyportals.com>
	 */

	static loadedTrackers() {
		// Make sure that we don't initialize this tracker twice
		const propName = '_loaded_trackers_snplw_tracker_initiated';
		if (window[propName]) {
			// tracking already initiated
			return;
		}
		window[propName] = true;

		window.addEventListener('load', this.detectTrackers, false);
	}

	static detectTrackers() {
		const ga = typeof window.ga === 'function' && window.ga.loaded === true;

		const gtm = typeof window['google_tag_manager'] === 'object';

		const hj = typeof window.hj === 'function' && window.hj['scriptLoaded'] === true;

		const mxpnl = typeof window['mixpanel'] === 'object' && window['mixpanel']['__loaded'] === true;

		window.snowplow('trackSelfDescribingEvent', {
			event: {
				schema: 'iglu:com.studyportals/loaded_trackers/jsonschema/1-0-0',
				data: {
					ga: ga,
					gtm: gtm,
					hj: hj,
					mxpnl: mxpnl
				}
			}
		});
	}

	/**
	 *
	 * Tracks link clicks as unstructured events with more information
	 * than the standard Snowplow link click event
	 *
	 */

	static enrichedLinkClicks() {
		// Make sure that we don't initialize this tracker twice
		const propName = '_enr_link_click_snplw_tracker_initiated';
		if (window[propName]) {
			// link click tracking already initiated
			return;
		}
		window[propName] = true;

		/*
		Listen to all clicks on the page and then keep only
		clicks on <a> elements.
		So we capture clicks on dynamically created links
		*/
		document.addEventListener('click', this.trackLinkClick);
	}

	/**
	 * Find the first div-parent with a className, check max 10 parents.
	 * If no div element has been found, do not return anything.
	 * @param {object} linkParent parent of the clicked link
	 * @returns {object} a <div> element or undefined
	 */
	static getLinkParentWithDiv(linkParent) {
		for (let i = 0; i < 10; i++) {
			if (!linkParent) {
				break;
			}

			if (linkParent.tagName === 'DIV' && linkParent.className) {
				return linkParent;
			}

			linkParent = linkParent.parentNode;
		}
	}

	static trackLinkClick(event) {
		const buttonClicked = event.button;

		const keysPressed = SPSnowplowTracker.listPressedKeys(event);

		const eventTarget = event.target;

		const linkDetails = SPSnowplowTracker.ensureLink(eventTarget);

		/* An <a> element, either:
		- the one that was clicked or
		- the ancestor of the non-<a> element that was clicked
		*/
		const linkClick = linkDetails.linkClick;

		let linkParent = linkDetails.linkParent;

		if (!linkClick) return;

		linkParent = SPSnowplowTracker.getLinkParentWithDiv(linkParent);

		// filter some characters from ga_tracking
		const gaTracking = SPSnowplowTracker.getExistingAttribute(linkClick, 'data-ga-tracking').replace(/[^(\w:|,)]/g, '');

		const data = {
			class: SPSnowplowTracker.getExistingAttribute(linkClick, 'class'),
			parentClass: SPSnowplowTracker.getExistingAttribute(linkParent, 'class'),
			href: SPSnowplowTracker.getExistingAttribute(linkClick, 'href'),
			title: SPSnowplowTracker.getExistingAttribute(linkClick, 'title'),
			target: SPSnowplowTracker.getExistingAttribute(linkClick, 'target'),
			gaTracking: gaTracking,
			mouseButton: buttonClicked,
			keysPressed: keysPressed
		};

		snowplow('trackSelfDescribingEvent', {
			event: {
				schema: 'iglu:com.studyportals/enriched_link_click/jsonschema/1-0-0',
				data: data
			}
		});
	}

	/**
	 * Converts e.g.
	 *
	 * {
	 *  ctrl: true,
	 *  shift: false,
	 *  alt: true,
	 *  meta: false
	 * }
	 *
	 * to:
	 *
	 * "ctrl,alt"
	 *
	 * @param {object} event
	 * @returns {string} a list of pressed keys
	 */
	static listPressedKeys(event) {
		const keys = {
			ctrl: event.ctrlKey,
			shift: event.shiftKey,
			alt: event.altKey,
			meta: event.metaKey
		};

		const filterFunction = function (key) {
			return keys[key];
		};

		return Object.keys(keys).filter(filterFunction).toString();
	}

	/**
	 * Ensures that an attribute is extracted only if it exists
	 * @param {object} name
	 * @param {string} attribute
	 * @returns {string} an attribute or empty string if the attribute does not exist
	 */
	static getExistingAttribute(name, attribute) {
		if (name && name.hasAttribute(attribute)) {
			return name.getAttribute(attribute);
		}

		// `replace` can only be called on the returned value if it is a string
		return '';
	}

	/**
	 * Check up to 10 parents whether it is a link.
	 * @param {object} eventTarget
	 * @returns {object} linkClick and linkParent
	 */
	static ensureLink(eventTarget) {
		let linkClick = null;
		let linkParent = null;

		for (let j = 0; j < 10; j++) {
			if (eventTarget.tagName === 'A') {
				linkClick = eventTarget;
				linkParent = eventTarget.parentNode;
				break;
			}
			if (eventTarget.parentNode !== null) {
				eventTarget = eventTarget.parentNode;
			} else {
				break;
			}
		}

		return {
			linkClick: linkClick,
			linkParent: linkParent
		};
	}

	/**
	 *
	 * Track whether an ad blocker is running or not
	 *
	 * @author Boris Morel <morel@studyportals.com>
	 * @author Alexandra Marginean <alexia@studyportals.com>
	 */

	static adblocker() {
		// Make sure that we don't initialize this tracker twice
		const propName = '_adblock_snplw_tracker_initiated';
		if (window[propName]) {
			// tracking already initiated
			return;
		}
		window[propName] = true;

		window.addEventListener('load', this.detectAdblocker, false);
	}

	static detectAdblocker() {
		snowplow('trackStructEvent', {
			category: 'Browser',
			action: 'Adblocker',
			label: SPSnowplowTracker.AdBlockEnabled(),
			property: 'tracker version=1.0.0'
		});
	}

	/**
	 *  Creates an HTML element with a class that would be blocked
	 *  by Adblock and checks whether the element was blocked or not.
	 *  @returns {string} whether an adblocker is running or not
	 */
	static AdBlockEnabled() {
		const ad = document.createElement('ins');
		ad.className = 'AdSense';
		ad.style.display = 'block';
		ad.style.position = 'absolute';
		ad.style.top = '-1px';
		ad.style.height = '1px';
		document.body.appendChild(ad);
		const isAdBlockEnabled = !ad.clientHeight;

		let eventValue = 'Unblocked';
		if (isAdBlockEnabled) {
			eventValue = 'Blocked';
		}

		// Clean up
		document.body.removeChild(ad);

		return eventValue;
	}

	/*
	 * Configure Snowplow tracker so some information is attached to every future events
	 * */
	static setUpGlobalContext() {
		const studyportalsId = this.getStudyportalsId();
		window['studyportalsId'] = studyportalsId;
		const context = {
			schema: 'iglu:com.studyportals/global_context/jsonschema/1-0-0',
			data: {
				user: {
					id: studyportalsId // Not to be confused with the identity ID of registered users
				}
			}
		};

		snowplow('addGlobalContexts', [context]);
	}

	/*
	 * Gets the user's UUID. The one assigned to every user, registered or not.
	 * It's also distinct from Snowplow's domain user ID.
	 * */
	static getStudyportalsId() {
		const trackData = trackingDataHandler.retrieveTrackingDetails();

		if (typeof trackData === 'object' && typeof trackData.uuid == 'string') {
			return trackData.uuid;
		}

		// Force the "id" field in the global context to be created
		return null;
	}
}

SPSnowplowTracker.init();
export default SPSnowplowTracker;
